import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 *
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  let cartItems = [];

  for (let cartItem of cartData) {
    let product = productsData.find((e) => e._id === cartItem.productId);
    // console.log("generateCartItemsFrom", cartItems, product)
    if (product) {
      const cartItemWithCompleteData = {
        name: product.name,
        category: product.category,
        cost: product.cost,
        rating: product.rating,
        image: product.image,
        productId: product._id,
        qty: cartItem.qty,
      };
      cartItems.push(cartItemWithCompleteData);
    }
  }
  // console.log("Cart Items from Cart component: ", cartItems);
  return cartItems;
};

// export const generateCartItemsFrom = (cartData, productsData) => {
//   if (!cartData) return;

//   const nextCart = cartData.map((item) => ({
//     ...item,
//     ...productsData.find((product) => item.productId === product._id),
//   }));

//   return nextCart;
// };

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  // Initialize a variable to store the total value
  let totalValue = 0;

  // Iterate through the cartItems array and calculate the total value
  for (const item of items) {
    // Assuming each item has 'cost' and 'qty' properties

    totalValue += item.cost * item.qty;
  }

  // Return the total value
  return totalValue;
};

/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 *
 */
const ItemQuantity = ({ value, handleAdd, handleDelete }) => {

  return (
    <Stack direction="row" alignItems="center">
      <IconButton size="small" color="primary" onClick={handleDelete}>
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton size="small" color="primary" onClick={handleAdd}>
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 *
 */
const Cart = ({ products, items, handleQuantity,isReadOnly =false }) => {
  
  const history = useHistory();
  const token = localStorage.getItem("token")

  // console.log(items);
  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }



  return (
    <>
      <Box className="cart">
        {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}

        {items.map((item, index) => (
          <Box
            display="flex"
            alignItems="flex-start"
            padding="1rem"
            key={index}
          >
            <Box className="image-container">
              <img
                // Add product image
                src={item.image}
                // Add product name as alt text
                alt={item.name}
                width="100%"
                height="100%"
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              height="6rem"
              paddingX="1rem"
            >
              <div>
                {/* Add product name */}
                {item.name}
              </div>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {isReadOnly && (<Box padding="0.5rem" fontWeight="700">  Qty: {item.qty} </Box>)}
                {!isReadOnly && (<ItemQuantity
                  // Add required props by checking implementation
                  value={item.qty}
                  handleAdd={() =>
                    handleQuantity(
                      token,
                      items,
                      products,
                      item.productId,
                      item.qty + 1
                    )
                  }
                  handleDelete={() =>
                    handleQuantity(
                      token,
                      items,
                      products,
                      item.productId,
                      item.qty - 1
                    )
                  }
                />)}
                <Box padding="0.5rem" fontWeight="700">
                  ${/* Add product cost */ item.cost}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}

        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" className="cart-footer">
          {!isReadOnly && (<Button
            color="primary"
            variant="contained"
            startIcon={<ShoppingCart />}
            className="checkout-btn"
            onClick={() => history.push("/checkout")}
          >
            Checkout
          </Button>)}
        </Box>

      </Box>


      
      
      
      {isReadOnly && <Box className="cart">
        <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
            marginLeft="12px"
            
          >
            Order Details
          </Box>

        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Products
          </Box>
          <Box
            color="#3C3C3C"
            // fontWeight="700"
            // fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            {items.length}
          </Box>
        </Box>
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >

          <Box color="#3C3C3C" alignSelf="center">
          Subtotal
          </Box>
          <Box
            color="#3C3C3C"
            // fontWeight="700"
            // fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Shipping Charges
          </Box>
          <Box
            color="#3C3C3C"
            // fontWeight="700"
            // fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            {0}
          </Box>
        </Box>
        <Box
          padding="1rem"
          display="flex"
          fontWeight="700"
            fontSize="1rem"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>

        </Box>}
    </>
    
  );
};

export default Cart;
